import { createStore, get, set, del, keys, values, type UseStore } from 'idb-keyval';
import type { Chat, ChatMessage } from '../types';
import { getBlob, getBlobWithProgress, getObject, isSyncConfigured } from '../lib/syncClient';
import {
  scheduleDeleteBlob,
  scheduleDeleteObject,
  schedulePutBlob,
  schedulePutObject,
} from '../lib/pushSync';

const audioStore = createStore('bedtimer-audio', 'audio');
const proseStore = createStore('bedtimer-prose', 'prose');
const outlineStore = createStore('bedtimer-outlines', 'outlines');
const chatStore = createStore('bedtimer-chats', 'chats');
const chatMetaStore = createStore('bedtimer-chat-meta', 'chat-meta');

async function getOrFetchBlob(
  store: UseStore,
  idbKey: string,
  serverKey: string
): Promise<Blob | undefined> {
  const local = await get<Blob>(idbKey, store);
  if (local !== undefined) return local;
  if (!isSyncConfigured()) return undefined;
  try {
    const blob = await getBlob(serverKey);
    if (!blob) return undefined;
    await set(idbKey, blob, store);
    return blob;
  } catch (e) {
    console.warn('lazy-load blob failed', serverKey, e);
    return undefined;
  }
}

async function getOrFetchText(
  store: UseStore,
  idbKey: string,
  serverKey: string
): Promise<string | undefined> {
  const local = await get<string>(idbKey, store);
  if (local !== undefined) return local;
  if (!isSyncConfigured()) return undefined;
  try {
    const blob = await getBlob(serverKey);
    if (!blob) return undefined;
    const text = await blob.text();
    await set(idbKey, text, store);
    return text;
  } catch (e) {
    console.warn('lazy-load text failed', serverKey, e);
    return undefined;
  }
}

async function getOrFetchChatMessages(
  idbKey: string
): Promise<ChatMessage[] | undefined> {
  const local = await get<ChatMessage[]>(idbKey, chatStore);
  if (local !== undefined) return local;
  if (!isSyncConfigured()) return undefined;
  try {
    const obj = await getObject<ChatMessage[]>('chat_messages', idbKey);
    if (!obj) return undefined;
    await set(idbKey, obj.json, chatStore);
    return obj.json;
  } catch (e) {
    console.warn('lazy-load chat_messages failed', idbKey, e);
    return undefined;
  }
}

async function getOrFetchAudioWithProgress(
  key: string,
  opts?: {
    signal?: AbortSignal;
    onProgress?: (loaded: number, total?: number) => void;
    onSource?: (src: 'local' | 'remote') => void;
  }
): Promise<Blob | undefined> {
  const local = await get<Blob>(key, audioStore);
  if (local !== undefined) {
    opts?.onSource?.('local');
    return local;
  }
  if (!isSyncConfigured()) return undefined;
  opts?.onSource?.('remote');
  const blob = await getBlobWithProgress(`audio_${key}`, {
    signal: opts?.signal,
    onProgress: opts?.onProgress,
  });
  if (!blob) return undefined;
  await set(key, blob, audioStore);
  return blob;
}

export const audio = {
  get: (key: string) => getOrFetchBlob(audioStore, key, `audio_${key}`),
  getWithProgress: getOrFetchAudioWithProgress,
  set: async (key: string, blob: Blob) => {
    await set(key, blob, audioStore);
    schedulePutBlob(`audio_${key}`, blob, blob.type || 'audio/mpeg');
  },
  del: async (key: string) => {
    await del(key, audioStore);
    scheduleDeleteBlob(`audio_${key}`);
  },
  keys: () => keys(audioStore),
};

export const prose = {
  get: (key: string) => getOrFetchText(proseStore, key, `prose_${key}`),
  set: async (key: string, text: string) => {
    await set(key, text, proseStore);
    schedulePutBlob(`prose_${key}`, text, 'text/plain; charset=utf-8');
  },
  del: async (key: string) => {
    await del(key, proseStore);
    scheduleDeleteBlob(`prose_${key}`);
  },
  keys: () => keys(proseStore),
};

export const outlines = {
  get: (key: string) => getOrFetchText(outlineStore, key, `outline_${key}`),
  set: async (key: string, text: string) => {
    await set(key, text, outlineStore);
    schedulePutBlob(`outline_${key}`, text, 'text/plain; charset=utf-8');
  },
  del: async (key: string) => {
    await del(key, outlineStore);
    scheduleDeleteBlob(`outline_${key}`);
  },
  keys: () => keys(outlineStore),
};

export const chats = {
  get: (key: string) => getOrFetchChatMessages(key),
  set: async (key: string, messages: ChatMessage[]) => {
    await set(key, messages, chatStore);
    schedulePutObject('chat_messages', key, messages);
  },
  del: async (key: string) => {
    await del(key, chatStore);
    scheduleDeleteObject('chat_messages', key);
  },
  keys: () => keys(chatStore),
};

export const chatMeta = {
  get: (key: string) => get<Chat>(key, chatMetaStore),
  set: (key: string, meta: Chat) => set(key, meta, chatMetaStore),
  del: (key: string) => del(key, chatMetaStore),
  keys: () => keys(chatMetaStore),
  all: () => values<Chat>(chatMetaStore),
};
